/* eslint-disable max-len */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { performMatrixOps } from './fuzzy-simplical-set';
import { computeMembershipStrengths } from './membership-strengths';
import { optimizationLoop } from './optimization-loop';
import { initializeSimplicialSetEmbedding } from './simplical-set-embedding';
import { smoothKNNDistance } from './smooth-knn-distance';
// TODO: FIT ASYNC METHOD WITH CALLBACKS on each step
// TODO: random function passing in params
export class UmapParams {
    constructor() {
        this.nComponents = 2;
        this.gamma = 1;
        this.alpha = 1;
        this.entryLen = 0;
        this.nNeighbours = 15;
        this.spread = 1;
        this.minDist = 0.1;
        this.negativeSampleRate = 5;
        this.localConnectivity = 1.0;
        this.setOpMixRatio = 1.0;
    }
}
export class WebGPUUMAP {
    constructor(entryLen, params) {
        this.params = new UmapParams();
        this.knnIndexes = null;
        this.knnDistances = null;
        this.rhos = null;
        this.sigmas = null;
        this.membershipStrengths = null;
        this.params.entryLen = entryLen;
        Object.assign(this.params, params);
    }
    setPrecomputedKNN(knnIndexes, knnDistances) {
        this.knnIndexes = knnIndexes;
        this.knnDistances = knnDistances;
    }
    // for now, not sure if we should include this, maybe webGPU UMAP should be stand alone.
    // eslint-disable-next-line max-len
    //   async calcKNN(entryList: SupportedEntryTypes[][], distanceMetrics: WEBGPUDISTANCE[], aggregationFunction: WEBGSLAGGREGATION, weights: number[], options: {
    //         [key: string]: any;
    //     }[]) {
    //     try {
    //       const knnRes = await multiColWebGPUKNN(entryList, this.params.nNeighbours, distanceMetrics, aggregationFunction, weights, options);
    //       if (knnRes) {
    //         this.knnIndexes = knnRes.knnIndexes;
    //         this.knnDistances = knnRes.knnDistances;
    //       }
    //     } catch (e) {
    //       console.error(e);
    //     }
    //     if (!this.knnIndexes || !this.knnDistances)
    //       throw new Error('failed to compute knn indexes and distances');
    //   }
    //   async calcKNNSingle(entryList: SupportedEntryTypes[], distanceMetrics: WEBGPUDISTANCE, options?: {
    //         [key: string]: any;
    //     }) {
    //     if (!options)
    //       options = {};
    //     await this.calcKNN([entryList], [distanceMetrics], WEBGSLAGGREGATION.MANHATTAN, [1], [options]);
    //   }
    initializeFit() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.calcSmoothKNNDistances();
            yield this.calcMembershipStrengths();
        });
    }
    fit() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.initializeFit();
            const umapParams = yield this.initializeSimplicialSetEmbedding();
            if (!umapParams)
                throw new Error('failed to compute umap simplistical set embeddings');
            const { head, tail, epochsPerSample, epochsPerNegativeSample, a, b, gamma, initialAlpha, nComponents, nEpochs, entryLen } = umapParams;
            const resEmbeddings = yield optimizationLoop(head, tail, entryLen, epochsPerSample, epochsPerNegativeSample, initialAlpha, gamma, a, b, nComponents, nEpochs, entryLen);
            return resEmbeddings;
        });
    }
    calcSmoothKNNDistances() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.knnIndexes || !this.knnDistances)
                throw new Error('knn indexes and distances must be set before calling fit');
            const res = yield smoothKNNDistance(this.knnDistances, this.params.nNeighbours, this.params.localConnectivity, 64);
            if (!res)
                throw new Error('failed to compute smooth knn distances');
            this.rhos = res.resultRhos;
            this.sigmas = res.resultSigmas;
        });
    }
    calcMembershipStrengths() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.knnIndexes || !this.knnDistances || !this.rhos || !this.sigmas)
                throw new Error('knn indexes, distances, rhos, and sigmas must be set before calling fit');
            const res = yield computeMembershipStrengths(this.knnDistances, this.sigmas, this.rhos);
            if (!res)
                throw new Error('failed to compute membership strengths');
            this.membershipStrengths = res;
        });
    }
    fuzzySimplicialSet() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.knnIndexes || !this.knnDistances || !this.rhos || !this.sigmas || !this.membershipStrengths)
                throw new Error('knn indexes, distances, rhos, sigmas, and membership strengths must be set before calling fit');
            const res = yield performMatrixOps(this.knnIndexes, this.membershipStrengths, this.params.setOpMixRatio);
            if (!res)
                throw new Error('failed to compute pairwise multiply with transpose');
            return res;
        });
    }
    initializeSimplicialSetEmbedding() {
        return __awaiter(this, void 0, void 0, function* () {
            const fuzzySimplicialSetRes = yield this.fuzzySimplicialSet();
            if (!fuzzySimplicialSetRes || !fuzzySimplicialSetRes.res || !fuzzySimplicialSetRes.unionMatrixOffsets || !fuzzySimplicialSetRes.unionSizes)
                throw new Error('failed to compute fuzzy simplicial set');
            const eRes = yield initializeSimplicialSetEmbedding(fuzzySimplicialSetRes, this.params.entryLen, this.params.spread, this.params.minDist, this.params.negativeSampleRate);
            if (!eRes)
                throw new Error('failed to compute umap simplistical set embeddings');
            return eRes;
        });
    }
}
//# sourceMappingURL=WEBGPU-UMAP.js.map